import {
  Badge,
  Box,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { buildGridLocaleText } from "@/utils/gridLocaleText";
import type {
  GridDataSource,
  GridListViewColDef,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import type { ListNotificationsFragment } from "@/gql/graphql";
import DataGrid from "@/components/DataGrid";
import Notification from "./Notification";
import { useReactiveUpdateDataGridRows } from "@/hooks/useReactiveUpdateDataGridRows";
import { useNotificationsService } from "@/hooks/service/useNotificationsService";
import {
  PermissionAction,
  PermissionCategory,
  PermissionGate,
} from "@/permissions";
import { Button, Icon, type ButtonProps } from "@trimble/modus-cmx";

function renderListViewCell(
  params: GridRenderCellParams<ListNotificationsFragment>,
  onNotificationClick: () => void
) {
  return (
    <Notification notification={params.row} onClick={onNotificationClick} />
  );
}

function NotificationMenu() {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (open) {
      apiRef.current?.scroll({ top: 0, left: 0 });
    }
  }, [open, apiRef]);

  const {
    hasUnReadNotifications,
    notificationsQuery: [, { data, loading }],
    markAllAsRead,
    fetchNotifications,
  } = useNotificationsService();

  useReactiveUpdateDataGridRows(
    apiRef,
    data?.notifications?.nodes ?? [],
    loading
  );

  const handleClick: NonNullable<ButtonProps["onButtonClick"]> = (event) => {
    if (event.currentTarget instanceof HTMLElement) {
      setAnchorEl(event.currentTarget);
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const listViewColDef: GridListViewColDef<ListNotificationsFragment> = {
    field: "listColumn",
    renderCell: (params) => renderListViewCell(params, handleClose),
  };

  const handleMarkAllAsRead = () => {
    void markAllAsRead();
  };

  const dataSource: GridDataSource = {
    getRows: async (params) => {
      const { start, end } = params;

      const notifications = await fetchNotifications(Number(start), end);

      const { nodes, totalCount: rowCount } = notifications ?? {};

      return {
        rows: nodes ?? [],
        rowCount,
      };
    },
  };

  return (
    <>
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <Button
          id="notification-button"
          shape="circle"
          color="neutral"
          variant="borderless"
          aria-label={t("notifications.notifications")}
          aria-controls={open ? "notification-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onButtonClick={handleClick}
        >
          <Icon name="notifications" />
        </Button>
        {hasUnReadNotifications && (
          <Badge
            variant="dot"
            color="secondary"
            sx={{
              position: "absolute",
              right: 10,
              top: 6,
              pointerEvents: "none",
            }}
          />
        )}
      </Box>
      <Menu
        id="notification-menu"
        anchorEl={anchorEl}
        open={open}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        onClose={handleClose}
        disableEnforceFocus
        slotProps={{
          list: {
            sx: {
              height: "100%",
              display: "flex",
              flexDirection: "column",
            },
            disablePadding: true,
            "aria-labelledby": "notification-button",
            subheader: (
              <ListSubheader sx={{ fontWeight: 700, color: "text.primary" }}>
                {t("notifications.notifications")}
              </ListSubheader>
            ),
          },
          paper: {
            sx: {
              width: 320,
              // Original height calculation was calc(100% - 96px)
              maxHeight: "min(calc(100% - 96px), 700px)",
              height: "100%",
            },
          },
        }}
      >
        <Divider />
        {open && (
          <DataGrid
            apiRef={apiRef}
            listView
            dataSource={dataSource}
            hideFooter
            lazyLoading
            listViewColumn={listViewColDef}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 5, page: 0 },
              },
            }}
            localeText={{
              ...buildGridLocaleText(t),
              noRowsLabel: t("notifications.no_notifications"),
            }}
            getRowHeight={() => "auto"}
            sx={{
              "& .MuiDataGrid-main": {
                border: 0,
              },
              "& .MuiDataGrid-row": {
                minHeight: "120px !important",
              },
              "--DataGrid-rowBorderColor": (theme) => theme.palette.divider,
              "--unstable_DataGrid-radius": 0,
            }}
            columns={[
              {
                field: "id",
              },
            ]}
          />
        )}
        <Divider />
        <PermissionGate
          resource={PermissionCategory.Notification}
          action={PermissionAction.CreateAndManage}
        >
          <MenuItem dense onClick={handleMarkAllAsRead}>
            {t("notifications.mark_all_as_read")}
          </MenuItem>
        </PermissionGate>
      </Menu>
    </>
  );
}
export default NotificationMenu;
